/**
 * Validates a standard cron expression (5 fields: minute hour day month day-of-week).
 * 
 * Standard cron format:
 * - Minute: 0-59
 * - Hour: 0-23
 * - Day of month: 1-31
 * - Month: 1-12 or JAN-DEC
 * - Day of week: 0-7 or SUN-SAT (0 and 7 are Sunday)
 * 
 * Supports:
 * - * (any value)
 * - , (value list separator)
 * - - (range of values)
 * - / (step values)
 * - ? (no specific value, used in day-of-month or day-of-week)
 * 
 * @param expression The cron expression to validate
 * @returns True if the expression is valid, false otherwise
 */
export function validateCronExpression(expression: string): boolean {
  if (!expression || !expression.trim()) {
    return false;
  }

  const trimmed = expression.trim();
  const parts = trimmed.split(/\s+/);

  // Standard cron has 5 fields
  if (parts.length !== 5) {
    return false;
  }

  // Validate each field
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  return (
    validateCronField(minute, 0, 59) &&
    validateCronField(hour, 0, 23) &&
    validateCronField(dayOfMonth, 1, 31, true) &&
    validateCronField(month, 1, 12, false, ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']) &&
    validateCronField(dayOfWeek, 0, 7, true, ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'])
  );
}

/**
 * Validates a single cron field.
 * 
 * @param field The field value to validate
 * @param min The minimum numeric value
 * @param max The maximum numeric value
 * @param allowQuestionMark Whether to allow ? (used for day-of-month or day-of-week)
 * @param monthNames Optional array of month/day names (e.g., ['JAN', 'FEB', ...])
 * @returns True if the field is valid
 */
function validateCronField(
  field: string,
  min: number,
  max: number,
  allowQuestionMark: boolean = false,
  monthNames?: string[]
): boolean {
  if (!field) {
    return false;
  }

  // Allow ? for day-of-month or day-of-week
  if (allowQuestionMark && field === '?') {
    return true;
  }

  // Handle wildcard
  if (field === '*') {
    return true;
  }

  // Handle step values (e.g., */5, 0-30/5)
  if (field.includes('/')) {
    const [range, step] = field.split('/');
    if (!step || isNaN(Number(step)) || Number(step) <= 0) {
      return false;
    }
    // Validate the range part
    if (range === '*') {
      return true;
    }
    return validateCronField(range, min, max, allowQuestionMark, monthNames);
  }

  // Handle ranges and lists
  const parts = field.split(',');
  for (const part of parts) {
    if (part.includes('-')) {
      // Range (e.g., 1-5, MON-FRI)
      const [start, end] = part.split('-');
      if (!validateCronValue(start, min, max, monthNames) || !validateCronValue(end, min, max, monthNames)) {
        return false;
      }
      const startNum = getNumericValue(start, monthNames);
      const endNum = getNumericValue(end, monthNames);
      if (startNum === null || endNum === null || startNum > endNum) {
        return false;
      }
    } else {
      // Single value
      if (!validateCronValue(part, min, max, monthNames)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Validates a single cron value (number or month/day name).
 */
function validateCronValue(value: string, min: number, max: number, monthNames?: string[]): boolean {
  if (!value) {
    return false;
  }

  // Check if it's a month/day name
  if (monthNames) {
    const upperValue = value.toUpperCase();
    if (monthNames.includes(upperValue)) {
      return true;
    }
  }

  // Check if it's a number
  const num = Number(value);
  if (isNaN(num)) {
    return false;
  }

  return num >= min && num <= max;
}

/**
 * Gets the numeric value for a cron field (handles month/day names).
 */
function getNumericValue(value: string, monthNames?: string[]): number | null {
  if (monthNames) {
    const upperValue = value.toUpperCase();
    const index = monthNames.indexOf(upperValue);
    if (index !== -1) {
      // For day-of-week, 0 is Sunday, so we need to handle that
      // For months, index + 1 is the month number
      return index;
    }
  }

  const num = Number(value);
  return isNaN(num) ? null : num;
}

/**
 * Gets a human-readable description of a cron expression.
 * 
 * @param expression The cron expression
 * @returns A description of what the cron expression does
 */
export function describeCronExpression(expression: string): string {
  if (!validateCronExpression(expression)) {
    return 'Invalid cron expression';
  }

  const parts = expression.trim().split(/\s+/);
  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  const descriptions: string[] = [];

  // Minute
  if (minute === '*') {
    descriptions.push('every minute');
  } else if (minute === '0') {
    descriptions.push('at minute 0');
  } else if (minute.includes('/')) {
    const step = minute.split('/')[1];
    descriptions.push(`every ${step} minutes`);
  } else {
    descriptions.push(`at minute ${minute}`);
  }

  // Hour
  if (hour === '*') {
    descriptions.push('every hour');
  } else if (hour === '0') {
    descriptions.push('at hour 0 (midnight)');
  } else if (hour.includes('/')) {
    const step = hour.split('/')[1];
    descriptions.push(`every ${step} hours`);
  } else {
    descriptions.push(`at hour ${hour}`);
  }

  // Day of month
  if (dayOfMonth === '*') {
    descriptions.push('every day of month');
  } else if (dayOfMonth === '?') {
    descriptions.push('any day of month');
  } else {
    descriptions.push(`on day ${dayOfMonth} of month`);
  }

  // Month
  if (month === '*') {
    descriptions.push('every month');
  } else {
    descriptions.push(`in month ${month}`);
  }

  // Day of week
  if (dayOfWeek === '*') {
    descriptions.push('every day of week');
  } else if (dayOfWeek === '?') {
    descriptions.push('any day of week');
  } else {
    descriptions.push(`on ${dayOfWeek}`);
  }

  return descriptions.join(', ');
}
